/**
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 *
 */

import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $applyNodeReplacement,
  $getSelection,
  $isNodeSelection,
  $setSelection,
  COMMAND_PRIORITY_LOW,
  DecoratorNode,
  KEY_BACKSPACE_COMMAND,
  KEY_DELETE_COMMAND,
  type LexicalNode,
  type NodeKey,
  SerializedLexicalNode,
  type Spread,
} from "lexical";
import { useLexicalNodeSelection } from "@lexical/react/useLexicalNodeSelection";
import { useEffect } from "react";
import { mergeRegister } from "../editor-utils";

export type SerializedMentionNode = Spread<
  {
    platformText: string;
    __text: string;
  },
  SerializedLexicalNode
>;

export interface IMentionNode extends DecoratorNode<React.ReactNode> {
  platformText: string;
  __text: string;
  isCustomMentionNode: true;
}

export const mentionFactory = ({
  nodeName,
  renderNode,
}: {
  nodeName: string;
  renderNode: (
    node: IMentionNode,
    context: { isSelected: boolean }
  ) => React.ReactNode;
}) => {
  class SubDecoratorNode extends DecoratorNode<React.ReactNode> {
    public platformText: string;
    public __text: string;
    public isCustomMentionNode = true as const;

    static getType(): string {
      return nodeName;
    }

    static clone(node: SubDecoratorNode): SubDecoratorNode {
      return new SubDecoratorNode(node.platformText, node.__text, node.__key);
    }
    static importJSON(serializedNode: SerializedMentionNode): SubDecoratorNode {
      return $createMentionNode(
        serializedNode.platformText,
        serializedNode.__text
      ).updateFromJSON(serializedNode);
    }

    constructor(platformText?: string, text?: string, key?: NodeKey) {
      super(key);
      this.__text = text || platformText || "";
      this.platformText = platformText || "";
    }

    exportJSON(): SerializedMentionNode {
      return {
        ...super.exportJSON(),
        platformText: this.platformText,
        __text: this.__text,
      };
    }

    createDOM(): HTMLElement {
      return document.createElement("span");
    }
    updateDOM(): false {
      return false;
    }

    decorate(): React.ReactNode {
      return <MentionNodeComponent mention={this} renderContent={renderNode} />;
    }
  }
  function $createMentionNode(
    mentionPlatformText: string,
    textContent?: string
  ): SubDecoratorNode {
    const mentionNode = new SubDecoratorNode(
      mentionPlatformText,
      textContent || mentionPlatformText
    );

    return $applyNodeReplacement(mentionNode);
  }

  function $isMentionNode(
    node: LexicalNode | null | undefined
  ): node is SubDecoratorNode {
    return node instanceof SubDecoratorNode;
  }
  return {
    NodeClass: SubDecoratorNode,
    $createMentionNode,
    $isMentionNode,
  };
};

function MentionNodeComponent({
  mention,
  renderContent,
  containerClassName,
}: {
  mention: IMentionNode;
  renderContent: (
    node: IMentionNode,
    context: { isSelected: boolean }
  ) => React.ReactNode;
  containerClassName?: string;
}) {
  const [editor] = useLexicalComposerContext();
  const [isSelected, setSelected, clearSelection] = useLexicalNodeSelection(
    mention.getKey()
  );

  useEffect(() => {
    const handleRemove = (event: KeyboardEvent) => {
      const selection = $getSelection();
      if (
        $isNodeSelection(selection) &&
        selection.getNodes().includes(mention)
      ) {
        event.preventDefault();
        editor.update(() => {
          mention.remove();
        });
        return true;
      }

      return false;
    };
    return mergeRegister(
      editor.registerCommand(
        KEY_BACKSPACE_COMMAND,
        handleRemove,
        COMMAND_PRIORITY_LOW
      ),
      editor.registerCommand(
        KEY_DELETE_COMMAND,
        handleRemove,
        COMMAND_PRIORITY_LOW
      )
    );
  });

  return (
    <span
      onClick={(e) => {
        // To prevent editor onClick from being triggered
        e.stopPropagation();
        editor.update(() => {
          if (isSelected) {
            clearSelection();
          } else {
            $setSelection(null);
            setSelected(true);
          }
        });
      }}
      data-before={`\u200A`} // thin-width space
      className={`inline relative 
      after:content-[attr(data-before)] before:content-[attr(data-before)] ${containerClassName || ""}`}
    >
      {renderContent(mention, { isSelected })}
    </span>
  );
}

export const $isGenericMentionNode = (
  node: LexicalNode | null | undefined
): node is IMentionNode => {
  return (
    node instanceof DecoratorNode && (node as IMentionNode).isCustomMentionNode
  );
};
